import { ButtonType, ButtonWidget, InlineAlertType, InlineAlertWidget, ToastType, ToastWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import TagHttpClient from '~src/httpClient/TAG/TagHttpClient';

import { openRemoveDomainDialog } from '../helpers/CustomDomainDialogUtility';
import { DeleteCustomDomainTagRequest } from '../serverAPI/requests/DeleteCustomDomainTagRequest';
import { CustomDomainSettingResponse, LastError, Status } from '../serverAPI/types/CustomDomainTypes';
import { configuredSettingAlertStyle, configuredSettingButtonStyle, configuredSettingContentStyle, configuredSettingHeaderStyle, configuredSettingValueStyle, replaceSslParentContainerStyle, replaceSslSubHeaderStyle, wordSpacingStyle } from '../styles/CustomDomainStyles';
import { UploadSSLCertificateView } from './UploadSSLCertificateView';


export interface ConfiguredCustomDomainState {
    isUploading: boolean;
    shouldReplaceSsl: boolean;
  }

export interface ConfiguredCustomDomainProps {
    customDomainSetting: CustomDomainSettingResponse;
    triggerFetchCustomDomain(): void;
  }

export class ConfiguredCustomDomainView extends React.PureComponent<ConfiguredCustomDomainProps, ConfiguredCustomDomainState> {
    public constructor(props: ConfiguredCustomDomainProps) {
        super(props);
        this.state = {
            isUploading: false,
            shouldReplaceSsl: false
        };
      }

      private handleCancelClick() {
          openRemoveDomainDialog(() => this.removeDomain());
      }

      private removeDomain = (): void => {
        this.setState({isUploading : true});
        TagHttpClient.sendTagRequest(new DeleteCustomDomainTagRequest({site_luid :this.props.customDomainSetting.site_luid}))
        .then((res: CustomDomainSettingResponse)  => {
          if(res.errors && res.errors.length > 0) {
            ToastWidget.show({message: Messages.CustomDomainSetting_deleteDomainFailedToast(), testId: 'toast-delete-operation-failed',toastType: ToastType.Error});
          } else {
            ToastWidget.show({message: Messages.CustomDomainSetting_removeDomainSucceededToast(), testId: 'toast-delete-operation-succeeded',toastType: ToastType.Success});
          }
           this.props.triggerFetchCustomDomain();
         }).catch(err => {
           ToastWidget.show({
            message: Messages.CustomDomainSetting_deleteDomainFailedToast(),
            testId: 'toast-validate-cname-operation-failed',
            toastType: ToastType.Error,
          });
         }).finally(() => {this.setState({isUploading: false});});
      };

      //This is to toggle ConfiguredView to UploadSslView.
      private handleReplaceSslClick = (): void  => {
        this.setState({shouldReplaceSsl: !this.state.shouldReplaceSsl});
      };

      private getCertExpirationDate = (): string => {
        return this.props.customDomainSetting.tls_certificate_expiry_date ? new Date(this.props.customDomainSetting.tls_certificate_expiry_date).toUTCString() : undefined;
      };

      private renderConfiguredCustomDomainView() {
        return (
              <>
                <div className={configuredSettingValueStyle}>
                  <strong>{Messages.CustomDomainSetting_configuredCustomDomain()}:</strong>
                  <span className={wordSpacingStyle}>{this.props.customDomainSetting.custom_domain}</span>
                </div>
                <div className={configuredSettingValueStyle}>
                  <strong>{Messages.CustomDomainSetting_configuredTableauDomain()}: </strong>
                  <span className={wordSpacingStyle}>{this.props.customDomainSetting.intermediate_domain}</span>
                </div>
                {!this.state.shouldReplaceSsl ?
                <div className={configuredSettingValueStyle}>
                  <strong>{Messages.CustomDomainSetting_configuredSslExpiry()}: </strong>
                  <span className={wordSpacingStyle}>{this.getCertExpirationDate()}</span>
                </div> :  null}
               </>
            );
      }

      private renderAlertText(alertText: string, testId: string) {
        return <span>{alertText}</span>;
      }

      private maybeRenderProvisionStatus() {
        switch(this.props.customDomainSetting.status) {
          case Status.provisioning:
            return this.renderAlert('inlineAlert_provisioning', InlineAlertType.Info, Messages.CustomDomainSetting_provision_inlineAlert());
          case Status.deleting:
            return this.renderAlert('inlineAlert_deleting', InlineAlertType.Info,  Messages.CustomDomainSetting_remove_inlineAlert());
          case Status.deletionFailed:
            return this.renderAlert('inlineAlert_deletionFailed', InlineAlertType.Error, Messages.CustomDomainSetting_removeFailed_inlineAlert());
          default:
            return <></>;
        }
      }

      private renderAlert(alertTestId: string, alertType: InlineAlertType, text: string) {
        return  <div className={configuredSettingAlertStyle}><InlineAlertWidget testId={alertTestId} type={alertType} children={this.renderAlertText(text, alertTestId)}/></div>;
      }

      private maybeRenderInlineAlert() {
        if(this.props.customDomainSetting.last_error && this.props.customDomainSetting.last_error === LastError.PROVISIONING_FAILED) {
          return this.renderAlert('inlineAlert_provisioningFailed', InlineAlertType.Error, Messages.CustomDomainSetting_provisionFailed_inlineAlert());
        }
        if(this.props.customDomainSetting.last_error && this.props.customDomainSetting.last_error === LastError.CERT_REFRESH_FAILED) {
          return this.renderAlert('inlineAlert_replaceFailed', InlineAlertType.Error, Messages.CustomDomainSetting_replaceTlsFailed_inlineAlert());
        }
        if (this.props.customDomainSetting.certificate_update_in_progress) {
          return this.renderAlert('inlineAlert_replaceInProgress', InlineAlertType.Info, Messages.CustomDomainSetting_replaceTls_inlineAlert());
        }
        return this.maybeRenderProvisionStatus();
      }

      private isCertBeingRemovedOrReplaced() {
        return this.props.customDomainSetting.certificate_update_in_progress || this.state.isUploading;
      }

    public render() {
        return <>
        <>
            {this.maybeRenderInlineAlert()}
            <div className={configuredSettingHeaderStyle}>
                <strong >{Messages.CustomDomainSetting_currentConfigurationTitle()}</strong>
            </div>
            <div className={configuredSettingContentStyle}>
              {this.renderConfiguredCustomDomainView()}
            </div>
            <div className={configuredSettingContentStyle}>
            {!this.state.shouldReplaceSsl ?
              <>
              <div style={{position: 'relative'}}>
                  <ButtonWidget buttonType={ButtonType.Destructive} showSpinner={this.isCertBeingRemovedOrReplaced()} onClick={() => this.handleCancelClick()} testId='button-remove-domain' className={configuredSettingButtonStyle}>
                    {Messages.CustomDomainSetting_removeDomainLabel()}
                  </ButtonWidget>
                  <ButtonWidget buttonType={ButtonType.Outline}  disabled={this.isCertBeingRemovedOrReplaced()}  onClick={this.handleReplaceSslClick} testId='button-replace-ssl' className={configuredSettingButtonStyle}>
                    {Messages.CustomDomainSetting_replaceSslButton()}
                  </ButtonWidget>
                </div>
              </> : <ButtonWidget buttonType={ButtonType.Destructive} onClick={this.handleReplaceSslClick} testId='button-replace-ssl-cancel' style={replaceSslParentContainerStyle}>
                    {Messages.cancel()}
                    </ButtonWidget>}
            </div>
          </>
          {this.state.shouldReplaceSsl ?
          <>
            <div style={replaceSslSubHeaderStyle}>
              <strong>{Messages.CustomDomainSetting_uploadSslDescription() }</strong>
            </div>
            <UploadSSLCertificateView customDomainSetting={this.props.customDomainSetting}  triggerFetchCustomDomain={this.props.triggerFetchCustomDomain} triggerCancelReplaceSsl={() => this.handleReplaceSslClick()}/>
          </> : null }
        </>;
    }
}